import User from '@/modules/user/models/User'
import SpaceVariable from '@/modules/space/models/SpaceVariable'
import { isOrphanFilledVariable } from '@/util/variable-state'

/**
 * Calculates a hash code for a string (same algorithm as UserAvatar)
 * @param s - The string to hash
 * @returns The hash code (always positive)
 */
export function hashCode(s: string): number {
  let hash = 0
  for (let i = 0; i < s.length; i++) {
    const chr = s.charCodeAt(i)
    hash = (hash << 5) - hash + chr
    hash |= 0
  }
  return Math.abs(hash)
}

/**
 * Calculates the color index for an avatar based on email hash
 * @param email - The email address to hash
 * @returns Color index (0-15)
 */
export function getAvatarColorIndex(email: string): number {
  return hashCode(email) % 16
}

/**
 * Extracts the initial for an avatar (matching UserAvatar xs size behavior)
 * @param user - Optional user object to get firstName from
 * @param email - Fallback email address
 * @returns Single uppercase letter initial
 */
export function getAvatarInitial(user: User | undefined, email: string): string {
  if (user?.firstName) {
    return user.firstName.charAt(0).toUpperCase()
  }
  return email.charAt(0).toUpperCase()
}

/**
 * Builds callout attributes for a variable with recipient assignment
 * @param recipientEmail - The recipient email/name
 * @param variable - Optional variable object to look up user
 * @param users - Optional users array to find user by ID
 * @returns Object with callout attributes
 */
export function buildCalloutAttributes(
  recipientEmail: string,
  variable?: SpaceVariable,
  users?: User[]
): Record<string, string> {
  const attrs: Record<string, string> = {
    'data-recipient-email': recipientEmail
  }

  // Get initial (prefer user firstName if available)
  let initial = recipientEmail.charAt(0).toUpperCase()
  if (variable?.assignedToRecipientId && users) {
    const user = users.find(u => u.id === variable.assignedToRecipientId)
    if (user) {
      initial = getAvatarInitial(user, recipientEmail)
    }
  }
  attrs['data-recipient-initial'] = initial

  // Calculate color index
  const colorIndex = getAvatarColorIndex(recipientEmail)
  attrs['data-recipient-color-index'] = colorIndex.toString()

  return attrs
}

/**
 * Builds assign-callout node attributes for a variable placeholder.
 * Uses recipient details when assigned; marks orphan-filled variables for reassign label.
 */
export function buildAssignCalloutAttributes(
  variable?: SpaceVariable,
  recipientEmail?: string,
  users?: User[]
): Record<string, string> {
  if (recipientEmail?.trim()) {
    return buildCalloutAttributes(recipientEmail, variable, users)
  }
  if (variable && isOrphanFilledVariable(variable)) {
    return { 'data-is-reassign': 'true' }
  }
  return {}
}
